import { useTheme } from '@mui/material/styles';
import { Badge, IconButton, Tooltip } from '@mui/material';
import { NotificationsNoneOutlined } from '@mui/icons-material';
import { useState, useEffect } from 'react';
import { NotificationPanel } from './NotificationPanel';
import { useUserNotifications } from 'hooks/useUserNotifications';
import { setMockLink } from 'graphql/config';
import { createUserNotificationMockLink } from './mocks/mockLink';

export const NotificationButton = () => {
    const [anchorNotificationEl, setAnchorNotificationEl] = useState<null | HTMLElement>(null);
    const theme = useTheme();

    // Activar mocks cuando el componente se monta
    const USE_MOCKS = true; // Cambiar a false cuando el API esté lista

    useEffect(() => {
        if (USE_MOCKS) {
            setMockLink(createUserNotificationMockLink());
        }

        return () => {
            if (USE_MOCKS) {
                setMockLink(null);
            }
        };
    }, [USE_MOCKS]);

    // Apollo integration (reemplaza Redux)
    const { notifications, unreadCount, markNotificationAsRead, toggleNotificationPin, toggleNotificationMute } = useUserNotifications();

    const handleNotificationClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorNotificationEl(event.currentTarget);
    };

    const handleNotificationClose = () => setAnchorNotificationEl(null);

    // Handlers for system notifications
    const handleMarkAsRead = (id: number) => {
        markNotificationAsRead(id);
    };

    const handleTogglePin = (id: number) => {
        toggleNotificationPin(id);
    };

    const handleMute = (id: number, module: string) => {
        toggleNotificationMute(id);
    };

    const handleCopyLink = (link: string) => {
        navigator.clipboard.writeText(link).then(() => {
            console.log('Link copied to clipboard:', link);
        });
    };

    const handleUnreadUpdatesClick = () => {
        // Filter to show only unread notifications
        console.log('Show unread updates');
    };

    return (
        <>
            <Tooltip title="Notifications">
                <IconButton
                    onClick={handleNotificationClick}
                    size="large"
                    data-testid="notification-button"
                    sx={{
                        backgroundColor: anchorNotificationEl ? theme.palette.secondary.light : theme.palette.primary[300],
                        marginRight: '0.5rem'
                    }}
                >
                    {unreadCount > 0 && (
                        <Badge
                            badgeContent={unreadCount}
                            data-testid="notification-badge"
                            sx={{
                                '&>span.MuiBadge-badge': {
                                    backgroundColor: theme.palette.error.main,
                                    color: 'white'
                                }
                            }}
                        >
                            <NotificationsNoneOutlined
                                data-testid="notification-icon"
                                sx={{ color: anchorNotificationEl ? theme.palette.secondary.main : theme.palette.primary[400] }}
                            />
                        </Badge>
                    )}
                    {unreadCount === 0 && (
                        <NotificationsNoneOutlined
                            data-testid="notification-icon"
                            sx={{ color: anchorNotificationEl ? theme.palette.secondary.main : theme.palette.primary[400] }}
                        />
                    )}
                </IconButton>
            </Tooltip>
            <NotificationPanel
                anchorEl={anchorNotificationEl}
                open={Boolean(anchorNotificationEl)}
                onClose={handleNotificationClose}
                notifications={notifications}
                unreadCount={unreadCount}
                onMarkAsRead={handleMarkAsRead}
                onTogglePin={handleTogglePin}
                onMute={handleMute}
                onCopyLink={handleCopyLink}
                onUnreadUpdatesClick={handleUnreadUpdatesClick}
            />
        </>
    );
};
